import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CityJobs } from "../components/CityJobs";
import { CityRecap } from "../components/CityRecap";
import { VStreetsHome } from "./VStreets";
import { PayoutStatement } from "../components/PayoutStatement";
import { MyProperties, RealEstateMarket, RealtorDesk } from "../components/CityProperties";
import { MyVehicles, CityRadio, CityWallet } from "../components/CityUniverse2";
import { motion } from "framer-motion";
import { Building2, Briefcase, Music4, Trophy, Calendar, ShieldCheck, Zap, Flame, Clock, Users, MapPin, Radio, Lock, BarChart3, ArrowUp, ArrowDown, Minus, BellPlus, BellRing, Home, KeyRound, Landmark, Car, Swords, Activity, Gauge } from "lucide-react";
import { api, errText } from "../lib/api";
import { Avatar, Loading, Empty, CreatorName } from "../components/common";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { CitizenLinks, VstreetsSyncStatus, ActivityFeed, RadioMusicLinks, CommandCenter } from "../components/CityHub";
import { UniverseAdmin } from "../components/UniverseAdmin";

// Hub: 9 sections, each with sub-tabs. URL: ?s=<section>&t=<sub>; legacy ?tab=<key> links still work.
const SECTIONS = [
  ["citizen", "Citizen Dashboard", ShieldCheck, [["overview", "Dashboard"], ["links", "Links & Characters"]]],
  ["vstreets", "Vstreets & Vcrews", Swords, [["vstreets", "Crew & NPCs"], ["sync", "Sync Status"]]],
  ["realestate", "Real Estate", Landmark, [["properties", "My Properties"], ["realestate", "Market"], ["realtor", "Realtor Desk", "realtor"]]],
  ["careers", "Careers & Businesses", Briefcase, [["jobs", "Job Center"], ["careers", "Careers"], ["businesses", "Businesses"]]],
  ["radio", "VStars Radio & Music", Radio, [["radio", "VStars Radio"]]],
  ["studio", "Studio & Entertainment", Music4, [["nightlife", "Nightlife"], ["charts", "City Charts"]]],
  ["vehicles", "Vehicles & Garages", Car, [["vehicles", "My Vehicles"]]],
  ["activity", "Citizen Activity", Activity, [["activity", "Activity"], ["leaderboard", "Fame & Progression"]]],
  ["admin", "Admin Command Center", Gauge, [["command", "Command Center"], ["tools", "City Tools"]], "admin"],
];
const SECTION_OF = Object.fromEntries(SECTIONS.flatMap(([s, , , subs]) => subs.map(([k]) => [k, s])));
const TIER_COLOR = { Icon: "text-gold", Superstar: "text-gold", Celebrity: "text-eblue", "Rising Star": "text-eblue", Local: "text-muted-foreground" };
const fmtMin = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m || 0}m`);

export default function VirtuCity() {
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [props, setProps] = useState(null);
  const [char, setChar] = useState(null);
  useEffect(() => { api.get("/virtucity/properties").then(({ data }) => setProps(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/virtucity/dashboard", { params: char ? { char } : {} }).then(({ data }) => setD(data)).catch(() => setD(false)); }, [char]);
  const allowed = (flag) => !flag || (flag === "realtor" && props?.is_realtor) || (flag === "admin" && user?.role === "admin");
  const sections = SECTIONS.filter((x) => allowed(x[4])).map(([k, l, I, subs]) => [k, l, I, subs.filter((x) => allowed(x[2]))]);
  const legacy = sp.get("tab");
  const sec = sections.find(([k]) => k === (sp.get("s") || SECTION_OF[legacy])) || sections[0];
  const sub = sec[3].find(([k]) => k === (sp.get("t") || legacy)) || sec[3][0];
  const go = (s, t) => setSp(t ? { s, t } : { s });
  const onTab = (k) => go(SECTION_OF[k] || "citizen", k);
  if (d === null) return <Loading />;
  if (d === false) return <Empty icon={Building2} title="VirtuCity is unavailable right now" />;
  const t = sub[0];
  return (
    <div className="px-4 md:px-6 py-5" data-testid="virtucity-page">
      <Hero d={d} />
      <div className="flex flex-col lg:flex-row gap-5 mt-5">
        <nav className="lg:w-60 shrink-0 flex lg:flex-col gap-1.5 overflow-x-auto no-scrollbar lg:sticky lg:top-4 lg:self-start" data-testid="vc-hub-nav">
          {sections.map(([k, l, I]) => (
            <button key={k} onClick={() => go(k)} data-testid={`vc-section-${k}`}
              className={`shrink-0 h-10 px-3.5 rounded-xl text-sm font-semibold flex items-center gap-2.5 text-left transition-colors ${sec[0] === k ? "btn-gold text-black" : "surface-2 text-muted-foreground hover:text-white"}`}>
              <I size={15} className="shrink-0" /><span className="whitespace-nowrap">{l}</span>
            </button>
          ))}
        </nav>
        <section className="flex-1 min-w-0" data-testid={`vc-section-panel-${sec[0]}`}>
          {sec[3].length > 1 && <div className="flex gap-1.5 mb-4 overflow-x-auto no-scrollbar" data-testid="vc-tabs">
            {sec[3].map(([k, l]) => <button key={k} onClick={() => go(sec[0], k)} data-testid={`vc-tab-${k}`}
              className={`shrink-0 h-8 px-3.5 rounded-full text-xs font-semibold transition-colors ${t === k ? "bg-white text-black" : "surface-2 text-muted-foreground hover:text-white"}`}>{l}</button>)}
          </div>}
          {t === "overview" && <>
            {d.characters?.length > 1 && <CharSwitcher d={d} onPick={setChar} />}
            <div className="mb-4"><CityWallet /></div><Overview d={d} props={props} onTab={onTab} />
            <PayoutsCard />
          </>}
          {t === "links" && <CitizenLinks />}
          {t === "careers" && <Careers d={d} />}
          {t === "properties" && <MyProperties />}
          {t === "realestate" && <RealEstateMarket />}
          {t === "realtor" && <RealtorDesk />}
          {t === "vehicles" && <MyVehicles />}
          {t === "radio" && <><CityRadio /><RadioMusicLinks /></>}
          {t === "businesses" && <Businesses />}
          {t === "nightlife" && <Nightlife />}
          {t === "leaderboard" && <Leaderboard />}
          {t === "activity" && <ActivityFeed />}
          {t === "jobs" && <CityJobs />}
          {t === "vstreets" && <VStreetsHome embedded />}
          {t === "sync" && <VstreetsSyncStatus />}
          {t === "charts" && <><CityRecap autoOpen={sp.get("recap") === "1"} /><CityCharts /></>}
          {t === "command" && <CommandCenter />}
          {t === "tools" && <UniverseAdmin />}
        </section>
      </div>
    </div>
  );
}

function Hero({ d }) {
  const { citizen, city } = d;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-3xl border border-gold/25 p-6 md:p-8 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,.22),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(74,142,255,.2),transparent_60%)] bg-[#07070c]" data-testid="vc-hero">
      <div className="text-[11px] tracking-[0.3em] text-gold font-semibold">OFFICIAL FIVEM WORLD</div>
      <h1 className="font-display font-bold text-4xl sm:text-5xl mt-2">Virtu<span className="text-gold">City</span></h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-lg">Your avatar's life in the city: career, fame, businesses and nightlife, synced live from the streets.</p>
      <div className="flex flex-wrap gap-2 mt-5">
        <Pill icon={Users} label={`${city.citizens} citizens`} testid="vc-stat-citizens" />
        <Pill icon={Radio} label={`${city.online} in the city now`} testid="vc-stat-online" live={city.online > 0} />
        <Pill icon={Music4} label={`${city.venues_open} venues open`} testid="vc-stat-venues" />
      </div>
      <div className="mt-5">
        {citizen.is_citizen
          ? <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold" data-testid="vc-citizen-badge"><ShieldCheck size={16} />Verified Citizen{citizen.discord_username ? ` · ${citizen.discord_username}` : ""}</span>
          : <Link to="/settings" data-testid="vc-become-citizen"><Button className="btn-gold text-black rounded-full"><Lock size={15} className="mr-1.5" />Become a Verified Citizen</Button></Link>}
      </div>
    </motion.div>
  );
}

function Pill({ icon: I, label, testid, live }) {
  return <span className="surface-2 rounded-full px-3 h-8 text-xs flex items-center gap-1.5" data-testid={testid}>
    {live ? <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> : <I size={13} className="text-gold" />}{label}</span>;
}

function Overview({ d, props, onTab }) {
  const p = d.profile;
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="surface rounded-2xl p-5 lg:col-span-2" data-testid="vc-profile-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground">Character</div>
            <div className="font-display font-bold text-xl" data-testid="vc-character-name">{p.character_name || "Not in the city yet"}</div>
          </div>
          {p.online && <span className="text-xs text-emerald-400 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />In the city</span>}
        </div>
        <div className="grid grid-cols-3 gap-3 mt-5">
          <Stat label="Level" value={p.level} testid="vc-level" />
          <Stat label="Fame" value={d.fame.toLocaleString()} sub={d.tier} subClass={TIER_COLOR[d.tier]} testid="vc-fame" />
          <Stat label="Playtime" value={fmtMin(p.playtime_min)} testid="vc-playtime" />
        </div>
        <div className="mt-5" data-testid="vc-xp-bar">
          <div className="flex justify-between text-xs text-muted-foreground mb-1.5"><span>{p.xp.toLocaleString()} XP</span><span>Level {p.level + 1} at {p.next_level_xp.toLocaleString()}</span></div>
          <div className="h-2.5 rounded-full surface-2 overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${Math.round(p.progress * 100)}%` }} transition={{ duration: 0.9 }} className="h-full" style={{ background: "linear-gradient(90deg,#4A8EFF,#D4AF37)" }} />
          </div>
        </div>
        <div className="mt-5 surface-2 rounded-xl p-4 flex items-center gap-3" data-testid="vc-current-job">
          <Briefcase size={18} className="text-gold" />
          <div><div className="text-xs text-muted-foreground">Current career</div>
            <div className="font-semibold">{p.job_label || p.job || "Unemployed"}{p.job_grade ? ` · ${p.job_grade}` : ""}</div></div>
        </div>
        {!d.citizen.is_citizen && <p className="text-xs text-amber-400 mt-4">Link Discord + FiveM in Settings to start syncing your city stats.</p>}
      </div>
      <div className="space-y-4">
        <button onClick={() => onTab("properties")} className="surface rounded-2xl p-5 w-full text-left hover:border-gold/30 border border-transparent transition-colors" data-testid="vc-overview-properties">
          <div className="font-semibold flex items-center gap-2 mb-2"><Home size={16} className="text-gold" />Your properties</div>
          {!props ? <p className="text-xs text-muted-foreground">Loading…</p> : props.properties.length === 0 ? <p className="text-xs text-muted-foreground">No property yet. Browse Real Estate or request a building in the city.</p> :
            props.properties.slice(0, 4).map((p) => <div key={p.id} className="flex justify-between text-sm py-1"><span className="truncate">{p.name}</span><span className="text-[11px] uppercase text-gold">{p.status}</span></div>)}
        </button>
        <div className="surface rounded-2xl p-5" data-testid="vc-owned-businesses">
          <div className="font-semibold flex items-center gap-2 mb-3"><Building2 size={16} className="text-gold" />Your businesses</div>
          {d.businesses_owned.length === 0 ? <p className="text-xs text-muted-foreground">You don't own a city business yet.</p> :
            d.businesses_owned.map((b) => <div key={b.external_id} className="flex justify-between text-sm py-1.5"><span>{b.name}</span><OpenTag open={b.is_open} /></div>)}
        </div>
        <div className="surface rounded-2xl p-5" data-testid="vc-city-events">
          <div className="font-semibold flex items-center gap-2 mb-3"><Calendar size={16} className="text-gold" />City events</div>
          {d.events.length === 0 ? <p className="text-xs text-muted-foreground">No upcoming city events.</p> :
            d.events.map((e) => <Link to="/events" key={e.id} className="block py-1.5 text-sm hover:text-gold">{e.title}<div className="text-[11px] text-muted-foreground">{new Date(e.datetime).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" })} · {e.attending_count} going{e.going ? " · You're going" : ""}</div></Link>)}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, subClass, testid }) {
  return <div className="surface-2 rounded-xl p-3" data-testid={testid}><div className="text-[11px] text-muted-foreground">{label}</div>
    <div className="font-display font-bold text-xl mt-0.5">{value}</div>{sub && <div className={`text-[11px] font-semibold ${subClass}`}>{sub}</div>}</div>;
}

const OpenTag = ({ open }) => <span className={`text-[11px] font-semibold ${open ? "text-emerald-400" : "text-muted-foreground"}`}>{open ? "OPEN" : "CLOSED"}</span>;

function useList(path) {
  const [l, setL] = useState(null);
  useEffect(() => { api.get(path).then(({ data }) => setL(data)).catch(() => setL([])); }, [path]);
  return l;
}

function Careers({ d }) {
  const jobs = useList("/virtucity/careers");
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 surface rounded-2xl p-5" data-testid="vc-job-market">
        <div className="font-semibold mb-3">City job market</div>
        {!jobs ? <Loading /> : jobs.length === 0 ? <Empty icon={Briefcase} title="No careers synced yet" /> :
          jobs.map((j) => <div key={j.job} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0" data-testid={`vc-job-${j.job}`}>
            <span className="font-medium">{j.label}</span><span className="text-xs text-muted-foreground">{j.count} citizens · <span className="text-emerald-400">{j.online} on duty</span></span></div>)}
      </div>
      <div className="surface rounded-2xl p-5" data-testid="vc-career-history">
        <div className="font-semibold mb-3 flex items-center gap-2"><Clock size={15} className="text-gold" />Your career history</div>
        {d.career_history.length === 0 ? <p className="text-xs text-muted-foreground">Your jobs in the city will appear here.</p> :
          d.career_history.map((h, i) => <div key={i} className="py-1.5 text-sm">{h.job_label || h.job}{h.job_grade ? ` · ${h.job_grade}` : ""}<div className="text-[11px] text-muted-foreground">{new Date(h.at).toLocaleDateString()}</div></div>)}
      </div>
    </div>
  );
}

function Businesses() {
  const list = useList("/virtucity/businesses");
  if (!list) return <Loading />;
  if (!list.length) return <Empty icon={Building2} title="No city businesses yet" hint="Businesses appear here once the city syncs them." />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {list.map((b) => (
        <div key={b.external_id} className="surface rounded-2xl p-5 hover:border-gold/30 border border-transparent transition-colors" data-testid={`vc-business-${b.external_id}`}>
          <div className="flex justify-between items-start"><div className="font-display font-semibold">{b.name}</div><OpenTag open={b.is_open} /></div>
          <div className="text-xs text-gold mt-1">{b.category}</div>
          {b.location && <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><MapPin size={12} />{b.location}</div>}
          <div className="text-xs text-muted-foreground mt-1">{b.employees || 0} employees</div>
          {b.owner && <Link to={`/${b.owner.username}`} className="flex items-center gap-2 mt-3 text-xs"><Avatar src={b.owner.avatar_url} name={b.owner.display_name} size={22} /><CreatorName user={b.owner} /></Link>}
        </div>
      ))}
    </div>
  );
}

function Nightlife() {
  const fetched = useList("/virtucity/nightlife");
  const [list, setList] = useState(null);
  useEffect(() => { setList(fetched); }, [fetched]);
  const toggle = async (v) => {
    try {
      await (v.is_following ? api.delete(`/virtucity/venues/${v.external_id}/follow`) : api.post(`/virtucity/venues/${v.external_id}/follow`));
      setList((l) => l.map((x) => x.external_id === v.external_id ? { ...x, is_following: !v.is_following, follower_count: x.follower_count + (v.is_following ? -1 : 1) } : x));
      if (!v.is_following) toast.success(`You'll be alerted when ${v.name} opens or a DJ goes live`);
    } catch (e) { toast.error(errText(e)); }
  };
  if (!list) return <Loading />;
  if (!list.length) return <Empty icon={Music4} title="The city is quiet" hint="Clubs, bars and lounges go live here when they open." />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {list.map((v) => {
        const pct = v.capacity ? Math.min(100, Math.round((v.crowd / v.capacity) * 100)) : 0;
        return (
          <div key={v.external_id} className={`relative rounded-2xl p-5 border ${v.is_open ? "border-gold/30 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,.14),transparent_65%)]" : "border-white/5"} surface`} data-testid={`vc-venue-${v.external_id}`}>
            <div className="flex justify-between items-start"><Link to={`/virtucity/venue/${encodeURIComponent(v.external_id)}`} className="font-display font-semibold hover:text-gold" data-testid={`vc-venue-link-${v.external_id}`}>{v.name}</Link>
              {v.is_open ? <span className="text-[11px] font-bold text-rose-400 flex items-center gap-1"><Flame size={12} />LIVE</span> : <OpenTag open={false} />}</div>
            <div className="text-xs text-muted-foreground capitalize mt-1">{v.kind}</div>
            {v.is_open && <>
              {v.dj && <div className="text-sm mt-3">DJ {(() => { const n = v.dj.replace(/^dj\s+/i, ""); return v.dj_user ? <Link to={`/${v.dj_user.username}`} className="text-gold font-semibold hover:underline">{n}</Link> : <span className="text-gold font-semibold">{n}</span>; })()}</div>}
              {v.now_playing && <div className="text-xs text-muted-foreground mt-0.5">Now playing: {v.now_playing}</div>}
              <div className="mt-3"><div className="flex justify-between text-[11px] text-muted-foreground mb-1"><span>{v.crowd} inside</span>{v.capacity > 0 && <span>{pct}% full</span>}</div>
                <div className="h-1.5 rounded-full surface-2 overflow-hidden"><div className="h-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#4A8EFF,#fb7185)" }} /></div></div>
            </>}
            <button onClick={() => toggle(v)} data-testid={`vc-venue-follow-${v.external_id}`}
              className={`mt-4 w-full h-9 rounded-full text-xs font-semibold flex items-center justify-center gap-1.5 ${v.is_following ? "btn-gold text-black" : "surface-2 hover:text-gold"}`}>
              {v.is_following ? <><BellRing size={13} />Following venue</> : <><BellPlus size={13} />Follow venue</>}
              <span className="opacity-70">· {v.follower_count || 0}</span>
            </button>
          </div>
        );
      })}
    </div>
  );
}

function Leaderboard() {
  const rows = useList("/virtucity/leaderboard");
  if (!rows) return <Loading />;
  if (!rows.length) return <Empty icon={Trophy} title="No ranked citizens yet" />;
  return (
    <div className="surface rounded-2xl divide-y divide-white/5" data-testid="vc-leaderboard">
      {rows.map((r, i) => (
        <div key={r.user.id} className="flex items-center gap-3 p-4" data-testid={`vc-rank-${i + 1}`}>
          <div className={`w-7 text-center font-display font-bold ${i < 3 ? "text-gold" : "text-muted-foreground"}`}>{i + 1}</div>
          <Link to={`/${r.user.username}`}><Avatar src={r.user.avatar_url} name={r.user.display_name} size={40} /></Link>
          <div className="min-w-0 flex-1">
            <Link to={`/${r.user.username}`} className="font-semibold text-sm hover:text-gold"><CreatorName user={r.user} /></Link>
            <div className="text-[11px] text-muted-foreground truncate">{r.character_name || "—"}{r.job_label ? ` · ${r.job_label}` : ""} · Lv {r.level}</div>
          </div>
          <div className="text-right"><div className="font-display font-bold">{r.fame.toLocaleString()}</div><div className={`text-[11px] font-semibold ${TIER_COLOR[r.tier]}`}>{r.tier}</div></div>
          {r.online && <span className="w-2 h-2 rounded-full bg-emerald-400" title="In the city" />}
        </div>
      ))}
    </div>
  );
}

function Movement({ m }) {
  if (m === "new") return <span className="text-[10px] font-bold text-gold">NEW</span>;
  if (m > 0) return <span className="text-emerald-400 flex items-center text-[11px]"><ArrowUp size={12} />{m}</span>;
  if (m < 0) return <span className="text-rose-400 flex items-center text-[11px]"><ArrowDown size={12} />{-m}</span>;
  return <Minus size={12} className="text-muted-foreground" />;
}

function CityCharts() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/virtucity/charts").then(({ data }) => setD(data)).catch(() => setD({ rows: [] })); }, []);
  if (!d) return <Loading />;
  return (
    <div data-testid="vc-charts">
      <div className="flex items-end justify-between mb-3">
        <div><div className="font-semibold">VirtuCity Club Chart</div><div className="text-[11px] text-muted-foreground">Most played in city clubs this week{d.week ? ` · ${d.week}` : ""} · bigger crowds count more</div></div>
      </div>
      {d.rows.length === 0 ? <Empty icon={BarChart3} title="No club plays yet this week" hint="Songs played in VirtuCity clubs are ranked here every week." /> : (
        <div className="surface rounded-2xl divide-y divide-white/5">
          {d.rows.map((r) => (
            <div key={r.rank} className="flex items-center gap-3 p-4" data-testid={`vc-chart-row-${r.rank}`}>
              <div className={`w-7 text-center font-display font-bold text-lg ${r.rank <= 3 ? "text-gold" : "text-muted-foreground"}`}>{r.rank}</div>
              <div className="w-6 flex justify-center"><Movement m={r.movement} /></div>
              {r.track?.cover_url ? <img src={r.track.cover_url} alt="" className="w-11 h-11 rounded-lg object-cover" /> : <div className="w-11 h-11 rounded-lg surface-2 flex items-center justify-center"><Music4 size={16} className="text-gold" /></div>}
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{r.title}</div>
                <div className="text-[11px] text-muted-foreground truncate">{r.artist_user ? <Link to={`/${r.artist_user.username}`} className="hover:text-gold">{r.artist || r.artist_user.display_name}</Link> : (r.artist || "Unknown artist")}</div>
              </div>
              <div className="text-right"><div className="font-display font-bold text-sm">{r.plays} {r.plays === 1 ? "play" : "plays"}</div><div className="text-[11px] text-muted-foreground">{r.venues} venue{r.venues === 1 ? "" : "s"} · {r.score} pts</div></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CharSwitcher({ d, onPick }) {
  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4" data-testid="vc-char-switcher">
      {d.characters.map((c) => (
        <button key={c.char_id} onClick={() => onPick(c.char_id)} data-testid={`vc-char-${c.char_id}`}
          className={`shrink-0 rounded-xl px-4 py-2 text-left border transition-colors ${d.profile.char_id === c.char_id ? "border-gold/60 bg-gold/10" : "border-white/10 surface-2 hover:border-white/25"}`}>
          <div className="text-sm font-semibold flex items-center gap-1.5">{c.character_name || c.char_id}{c.online && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}</div>
          <div className="text-[11px] text-muted-foreground">Lv {c.level}{c.job_label ? ` · ${c.job_label}` : ""}</div>
        </button>
      ))}
    </div>
  );
}

function PayoutsCard() {
  const [p, setP] = useState(null);
  const [stmt, setStmt] = useState(false);
  useEffect(() => { api.get("/virtucity/payouts").then(({ data }) => setP(data)).catch(() => {}); }, []);
  if (!p) return null;
  return (
    <div className="surface rounded-2xl p-5 mt-4" data-testid="vc-payouts-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold">City payouts to VirtuCoins</div>
          <div className="text-[11px] text-muted-foreground">{Number(p.city_per_vc).toLocaleString()} city money = 1 VC · up to {p.daily_cap_vc} VC per day</div>
        </div>
        {p.eligible
          ? <div className="text-right" data-testid="vc-payouts-today"><div className="font-display font-bold text-xl text-gold">{Math.min(p.today_vc, p.daily_cap_vc)} / {p.daily_cap_vc} VC</div><div className="text-[11px] text-muted-foreground">paid today</div></div>
          : <Link to="/plus" data-testid="vc-payouts-upgrade"><Button className="btn-gold text-black rounded-full h-9">Get VirtuStars+ to earn VC</Button></Link>}
      </div>
      <button onClick={() => setStmt(true)} className="text-xs text-gold mt-3 hover:underline" data-testid="vc-statement-open">View monthly statement</button>
      {stmt && <PayoutStatement onClose={() => setStmt(false)} />}
      {p.history.length > 0 && <div className="mt-3 space-y-1">{p.history.slice(0, 5).map((h) => (
        <div key={h.id} className="text-xs flex justify-between surface-2 rounded-lg px-3 py-1.5"><span className="capitalize">{h.kind}{h.reason ? ` · ${h.reason}` : ""}</span>
          <span className={h.result.ok ? "text-emerald-400" : "text-muted-foreground"}>{h.result.ok ? `+${h.result.vc_credited} VC` : h.result.error.replace(/_/g, " ")}</span></div>))}</div>}
    </div>
  );
}
